/**
 * Phase 16 / Task 16.6 (DEC-163) — Disposal BI panels MVP Zod schemas.
 *
 * Backs three endpoints:
 *   GET  /v1/disposal-portal/bi/volume-trends?range=30D
 *   GET  /v1/disposal-portal/bi/acceptance-by-site?range=30D
 *   POST /v1/disposal-portal/bi/data-export
 *
 * `range` is a fixed enum (7D/30D/60D/180D/1Y/ALL). The route layer resolves
 * the enum to a concrete (dateFrom, dateTo) pair using the disposal_company's
 * canonical timezone; ALL falls back to a 5-year window (chart pragmatism —
 * a truly-unbounded query pushes SQL cost onto every request).
 *
 * `data-export` accepts explicit YYYY-MM-DD dates + a format enum. Route-layer
 * guard rejects > 365-day ranges (DISPOSAL_BI_DATE_RANGE_TOO_LARGE); we do NOT
 * enforce that in Zod because it's a runtime policy tied to product tier, not
 * a shape concern.
 */

import { z } from 'zod';

export const BI_DATE_RANGES = ['7D', '30D', '60D', '180D', '1Y', 'ALL'] as const;
export type BiDateRange = (typeof BI_DATE_RANGES)[number];

export const VolumeTrendsQuery = z
  .object({
    range: z.enum(BI_DATE_RANGES).default('30D'),
  })
  .strict();
export type VolumeTrendsQuery = z.infer<typeof VolumeTrendsQuery>;

export const AcceptanceBySiteQuery = z
  .object({
    range: z.enum(BI_DATE_RANGES).default('30D'),
  })
  .strict();
export type AcceptanceBySiteQuery = z.infer<typeof AcceptanceBySiteQuery>;

const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');

export const DataExportRequest = z
  .object({
    format: z.enum(['CSV', 'EXCEL']),
    dateFrom: IsoDate,
    dateTo: IsoDate,
  })
  .strict()
  .refine((v) => v.dateTo >= v.dateFrom, {
    message: 'dateTo must be on or after dateFrom',
    path: ['dateTo'],
  });
export type DataExportRequest = z.infer<typeof DataExportRequest>;

// ────────────────────────────────────────────────────────────────────────
// Response DTOs — Recharts-friendly shapes so the panel components can
// consume them without post-processing.
// ────────────────────────────────────────────────────────────────────────

/**
 * One entry in the series legend — `key` is a stable letter label
 * (A/B/C/D/E/Other), `color` is a preset hex string the panel component
 * hands to Recharts. Content service always emits the same 6 colors in
 * the same order so the front-end doesn't need a color assignment step.
 */
export interface BiSeriesMeta {
  key: string;
  label: string;
  color: string;
}

/**
 * One row of the multi-series line chart. `date` is a local ISO date
 * (YYYY-MM-DD, using the disposal_company's canonical timezone). Series
 * values are keyed by the same string as `BiSeriesMeta.key`.
 */
export interface BiVolumeTrendsPoint {
  date: string;
  [seriesKey: string]: string | number;
}

export interface BiVolumeTrendsResponse {
  range: BiDateRange;
  dateFrom: string;
  dateTo: string;
  series: BiSeriesMeta[];
  points: BiVolumeTrendsPoint[];
}

export interface BiAcceptanceSiteRow {
  locationId: string;
  siteName: string;
  seriesKey: string;
  seriesLabel: string;
  plannedCount: number;
  actualCount: number;
  rate: number;
}

export interface BiAcceptanceBySiteResponse {
  range: BiDateRange;
  dateFrom: string;
  dateTo: string;
  sites: BiAcceptanceSiteRow[];
}

export interface BiDataExportResponse {
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}
